import { isAnonymousStudioPath } from "./auth/rules/anonymousPaths";

/**
 * StudioWarmOptions carry what the warming decision needs without pulling the router's
 * generics into this module: the pathname the app booted on, a thunk that performs the
 * preload (so the typed `to` stays in the entry, where the router registration applies),
 * and a reader for the router's settle status. The two thunks are read at callback time,
 * not at schedule time, so the status reflects the moment the browser went idle.
 */
interface StudioWarmOptions {
  pathname: string;
  preload: () => Promise<unknown>;
  status: () => "idle" | "pending";
}

/**
 * scheduleStudioWarm preloads the studio route — the heaviest chunk, carrying the map and
 * MapLibre — once the browser is idle, so a user who lands on login or an IAM page has it
 * warm by the time they navigate in. It runs after first paint and never blocks it. Safari
 * has no requestIdleCallback, so fall back to a short timer there.
 *
 * The idle callback carries no timeout on purpose: warming is speculative, for a route the
 * user may never open, so a browser that stays busy should skip it rather than be forced to
 * run it.
 *
 * Two guards keep the warming from harming the page it is warming for:
 *
 * The public studio surfaces never schedule it at all. Preloading /studio would run the
 * root's beforeLoad and fire the Ory whoami the embed and the share view deliberately skip
 * — which also resets the identity store those surfaces seed — and both already load the
 * studio chunk through their own route. The check is the same predicate the root guard
 * skips on, so a new public surface is covered in both places at once.
 *
 * A preload is also skipped while the router is still resolving. The /studio preload runs
 * the _app guard, which throws a redirect for an anonymous visitor, and the resulting
 * teardown evicts the in-flight match the page is waiting on — leaving a route match
 * pending on a promise that no longer exists, which unmounts the tree. Speculative work
 * must never race the navigation the user actually asked for.
 */
export const scheduleStudioWarm = ({ pathname, preload, status }: StudioWarmOptions): void => {
  if (isAnonymousStudioPath(pathname)) {
    return;
  }

  const warm = () => {
    if (status() !== "idle") {
      return;
    }

    // Best-effort chunk warming: an anonymous visitor's /studio preload still surfaces as
    // a rejected promise (the _app guard's redirect). Warming is optional, so swallow it
    // rather than leak an unhandled rejection to the console.
    void preload().catch(() => {});
  };

  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(warm);
  } else {
    window.setTimeout(warm, 2000);
  }
};
